import { UseFormReturn } from "react-hook-form"
import type React from "react"
import { 
    ContentFile, 
    Model, 
    User, 
    Message,
    SubMode
} from "@/lib/types"
import { z } from "zod"

export type PromptMode = "text" | "image"




// Auth-related limits
export const UNAUTH_MAX_PROMPT_LENGTH = 10000

// This is the new, unified schema for our form.
// z.discriminatedUnion is the key here. It looks at the "mode" field
// and applies the correct validation based on its value.
export const textFormSchema = z.object({
    mode: z.literal("text"),
    prompt: z.string().trim().min(1, "Message cannot be empty."),
    model: z.custom<Model>(),
    files: z.array(z.custom<ContentFile>()).optional(),
})

export type TextFormValues = z.infer<typeof textFormSchema>


// DO NOT DELETE THIS COMMENT UNDER ANY CIRCUMSTANCES AND DO NOT IMPLEMENT IT ELSE WHERE!!!!
// START OF THE COMMENT 
// export type PromptMode = "chat" //| "edit-image" | "text-to-image" | "style-transfer" | "remove-object"

    // "edit-image": z.object({
    //     mode: z.literal("edit-image"),
    //     prompt: z.string().min(1, "Edit instruction cannot be empty."),
    //     modelId: z.custom<ModelId>(),
    //     files: z.array(z.custom<ContentFile>()).optional(),
    // }),
    // "text-to-image": z.object({
    //     mode: z.literal("text-to-image"),
    //     prompt: z.string().min(1, "Image description cannot be empty."),
    //     modelId: z.custom<ModelId>(),
    //     files: z.array(z.custom<ContentFile>()).optional(),
    // }),
    // "style-transfer": z.object({
    //     mode: z.literal("style-transfer"),
    //     prompt: z.string().min(1, "Style description cannot be empty."),
    //     modelId: z.custom<ModelId>(),
    //     files: z.array(z.custom<ContentFile>()).optional(),
    // }),
    // "remove-object": z.object({
    //     mode: z.literal("remove-object"),
    //     prompt: z.string().min(1, "Object description cannot be empty."),
    //     modelId: z.custom<ModelId>(),
    //     files: z.array(z.custom<ContentFile>()).optional(),
    // }),
// END OF THE COMMENT 




export interface UsePromptFormReturn {
    // form management
    form: UseFormReturn<PromptFormValues>
    formSchema: PromptFormSchemaMap[PromptMode]
    formValues: PromptFormValues
    onSubmit: (data: PromptFormValues) => void
    handleEditMessage: (message: Message) => void
    handleCancelEdit: () => void

    // functions
    getPlaceholderForMode: () => string
    onTextAreaFocus: () => void
    onTextAreaBlur: () => void
    handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void

    // state 
    currentMode: PromptMode
    onModeChange: (mode: PromptMode) => void
    // sub-modes
    availableSubModes: SubMode[]
    currentSubMode: SubMode | null
    onSubModeChange: (subMode: SubMode) => void
    clearSubMode: () => void

    
    isTextAreaFocused: boolean
    setIsTextAreaFocused: (isTextAreaFocused: boolean) => void
    isEditing: boolean
    
    // selectedModel: Model
    handleModelChange: (model: Model) => void

    // ui elements 
    userContextValue: User | null
    isUserAuthenticated: boolean
    maxTextAreaLines: number
    
    // file upload & drag state
    isDragActive: boolean
    setIsDragActive: (value: boolean) => void
    isUploading: boolean
    setIsUploading: (value: boolean) => void
}

export type PromptFormValues = PromptFormValuesMap[PromptMode]

// -------------------- IMAGE MODE --------------------

export const imageFormSchema = z.object({
    mode: z.literal("image"),
    prompt: z.string().trim().min(1, "Image description cannot be empty."),
    model: z.custom<Model>(),
    sourceImage: z.custom<ContentFile | null>().nullable().optional(),
})

export type ImageFormValues = z.infer<typeof imageFormSchema>
export type PromptFormValuesMap = {
    text: TextFormValues
    image: ImageFormValues
}

export type PromptFormSchemaMap = {
    [K in PromptMode]: z.ZodType<PromptFormValuesMap[K]>
}

export const promptFormSchema: PromptFormSchemaMap = {
    text: textFormSchema,
    image: imageFormSchema,
}

// Build schema with conditional refiners for guests
export function buildPromptFormSchema(isAuthenticated: boolean): PromptFormSchemaMap {
    if (isAuthenticated) {
        return promptFormSchema
    }

    return {
        text: promptFormSchema.text.superRefine((data, ctx) => {
            const text = (data.prompt || '').trim()
            if (text.length > UNAUTH_MAX_PROMPT_LENGTH) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Message cannot exceed ${UNAUTH_MAX_PROMPT_LENGTH.toLocaleString()} characters when not signed in.`,
                    path: ['prompt']
                })
            }

            const hasFiles = Array.isArray(data.files) && data.files.length > 0
            if (hasFiles) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'File uploads require an account. Please sign in.',
                    path: ['files']
                })
            }
        }) as PromptFormSchemaMap["text"],
        image: promptFormSchema.image.superRefine((data: any, ctx) => {
            // Guests cannot upload files or source images
            const hasFiles = Array.isArray(data.files) && data.files.length > 0
            const hasSource = !!data.sourceImage
            if (hasFiles || hasSource) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'File uploads require an account. Please sign in.',
                    path: ['files']
                })
            }
        }) as PromptFormSchemaMap["image"],
    }
}

// Unified discriminated union schema builder for the whole form
export function buildPromptUnionSchema(isAuthenticated: boolean) {
    // Always build the union from the base object schemas to satisfy
    // discriminatedUnion requirements (participants must be objects).
    const baseUnion = z.discriminatedUnion('mode', [textFormSchema, imageFormSchema])

    if (isAuthenticated) {
        return baseUnion
    }

    // Apply guest constraints at the union level to avoid wrapping the
    // individual object schemas with ZodEffects (which breaks the union).
    return baseUnion.superRefine((data, ctx) => {
        if (data.mode === 'text') {
            const text = (data.prompt || '').trim()
            if (text.length > UNAUTH_MAX_PROMPT_LENGTH) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Message cannot exceed ${UNAUTH_MAX_PROMPT_LENGTH.toLocaleString()} characters when not signed in.`,
                    path: ['prompt']
                })
            }
            const hasFiles = Array.isArray(data.files) && data.files.length > 0
            if (hasFiles) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'File uploads require an account. Please sign in.',
                    path: ['files']
                })
            }
        }
        if (data.mode === 'image') {
            const hasFiles = Array.isArray((data as any).files) && (data as any).files.length > 0
            const hasSource = !!data.sourceImage
            if (hasFiles || hasSource) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'File uploads require an account. Please sign in.',
                    path: ['files']
                })
            }
        }
    })
}







